import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { HashLink } from 'react-router-hash-link';
import { Grid, Typography, Button, Hidden, ButtonGroup } from '@material-ui/core';
import { Rating, Skeleton } from '@material-ui/lab';
import CallIcon from '@material-ui/icons/Call';
import CreditCardIcon from '@material-ui/icons/CreditCard';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import {
  ROUTE_PROFILE_SUBSCRIPTION_DETAIL,
  ROUTE_RESULTS,
  ROUTE_DISCOVER,
  ROUTE_DISCOVER_SEARCH,
  ROUTE_PARTNER_DISCOVER,
  ROUTE_SUBSCRIPTION,
} from '@/constants/routes';
import { LOCATIONS_APP_PAGE } from '@/constants/locations';
import { appsListStatuses } from '@/constants/app';
import { actions, triggered } from '@/constants/analytics';

// UTILITY
import { updateUrl } from '@/utility/common';
import { SubscriptionBtnNameVariant } from '@/utility/split';
import { formatPhoneNumber } from '@/utility/content';
import { formatPrice } from '@/utility/currency';
import {
  analyticsTrackTreatment,
  externalLink,
  getExternalUrlParams,
  analyticsInternalLinks,
  analyticsShareClick,
} from '@/utility/analytics';
import { getLink, routeEnhance, getStringifiedUrl } from '@/utility/routes';
import { checkMultipleAppsRewardType } from '@/utility/streamingRewards';

// HOC
import Guard from '@/hoc/Guard';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import RouterBreadcrumbs from '@/components/RouterBreadcrumbs';
import Link from '@/components/Link';
import AddRemoveButton from '@/components/Buttons/AddRemoveButton';
import Share from '@/components/Share';
import Loader from '@/components/Loader';
import AppDescription from '@/components/AppDescription';

// STYLES
import {
  HeroContainer,
  Darkness,
  Container,
  MainInfo,
  AppLogo,
  AnchorsContainer,
  PriceContainer,
  ButtonsContainer,
  ButtonStyled,
  SocialWrapper,
  SocialContainer,
  DisclaimerWrapper,
} from './styles';

class Hero extends PureComponent {
  componentDidMount() {
    this.trackTreatment();
  }

  trackTreatment = () => {
    const { treatments, data, user, virtualCard, partner, isSimpleView } = this.props;
    const appData = data.app || {};
    const hasPartner = !!Object.keys(partner).length;
    const isSubscribed = data.user_subscription !== null;
    const rewardData = user?.eligible_streaming_reward || {};
    const useVirtualCardForSubscription = Boolean(virtualCard?.allowed_for_subscriptions);
    const useSRCard =
      checkMultipleAppsRewardType(rewardData) &&
      ((hasPartner && partner.slug === rewardData.partner) ||
        (!hasPartner && rewardData.partner === null)) &&
      useVirtualCardForSubscription;
    const partnerSettings = partner?.settings || {};
    let useSubscription = true;

    if (hasPartner) {
      useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
    }
    const subscriptionIsAvailable = useSubscription && appData.has_subscription && !isSimpleView;

    function runAnalytic(split, aData = {}) {
      const properties = { split, treatment: treatments[split].treatment, ...aData };

      analyticsTrackTreatment(properties);
    }

    if (subscriptionIsAvailable) {
      if (
        treatments.subscription_btn_name &&
        !isSubscribed &&
        !useSRCard &&
        !appData.external_button_text
      ) {
        runAnalytic('subscription_btn_name');
      }
    }
  };

  getPrice = (price, type = 'month') => {
    const { t } = this.props;
    const isYear = type === 'year';

    return price
      ? t(`common.${isYear ? 'costYear' : 'costMonth'}`, { cost: formatPrice(price) })
      : t('common.free');
  };

  renderPrice = () => {
    const {
      data: { app },
    } = this.props;
    let priceContent = null;
    let annualPriceContent = null;

    if (app.price || app.price === 0) {
      priceContent =
        app?.deal_price || app?.deal_price === 0 ? (
          <div className="price-container__deal-container">
            <span className="price-container__cost">{this.getPrice(app.price)}</span>
            <span className="price-container__deal-cost">{this.getPrice(app.deal_price)}</span>
          </div>
        ) : (
          <span className="price-container__cost">{this.getPrice(app.price)}</span>
        );
    }

    if (app?.annual_price || app?.annual_price === 0) {
      annualPriceContent =
        app?.annual_deal_price || app?.annual_deal_price === 0 ? (
          <div className="price-container__deal-container">
            <span className="price-container__cost">{this.getPrice(app.annual_price, 'year')}</span>
            <span className="price-container__deal-cost">
              {this.getPrice(app.annual_deal_price, 'year')}
            </span>
          </div>
        ) : (
          <span className="price-container__cost">{this.getPrice(app.annual_price, 'year')}</span>
        );
    }

    return (
      <>
        <PriceContainer className="price-container">
          {priceContent}
          <i className="icon-vertical-slash" />
        </PriceContainer>
        {!!annualPriceContent && (
          <PriceContainer className="price-container">
            {annualPriceContent}
            <i className="icon-vertical-slash" />
          </PriceContainer>
        )}
      </>
    );
  };

  renderAnchorLinks = () => {
    const {
      t,
      router: {
        location: { pathname, search },
      },
      data,
    } = this.props;
    const content = [];

    if (!data?.app) return content;

    Object.keys(data.app).map(key => {
      if (!data.app[key]) {
        return;
      }

      const linkData = routeEnhance(pathname + search);
      const link = linkData.search ? `${linkData.pathname}?${linkData.search}` : linkData.pathname;

      switch (key) {
        case 'rating_count':
          content.push(
            <div key={key}>
              <HashLink smooth className="disable-underline" to={`${link}#ratings`}>
                <Rating value={data.app.rating / 20} precision={0.1} readOnly />
              </HashLink>
              <i className="icon-vertical-slash" />
            </div>,
          );

          break;

        case 'packeges_count':
          content.push(
            <div key={key}>
              <HashLink smooth to={`${link}#packages`}>
                {t(
                  `appDetails.hero.anchor.${data.app.packeges_count > 1 ? 'packages' : 'package'}`,
                  { count: data.app.packeges_count },
                )}
              </HashLink>
              <i className="icon-vertical-slash" />
            </div>,
          );
          break;

        case 'deals_count':
          content.push(
            <div key={key}>
              <HashLink smooth to={`${link}#deals`}>
                {t(`appDetails.hero.anchor.${data.app.deals_count > 1 ? 'deals' : 'deal'}`, {
                  count: data.app.deals_count,
                })}
              </HashLink>
              <i className="icon-vertical-slash" />
            </div>,
          );
          break;

        default:
          break;
      }

      return key;
    });

    return content;
  };

  handleVisitSite = () => {
    const {
      data: { app },
      partner,
      unique_id,
      segmentUserId,
    } = this.props;
    const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || segmentUserId });
    const href = updateUrl(app.url, externalUrlParams);

    const properties = {
      location: 'app page',
      href,
      app_id: app.id,
      type: 'app',
      service_name: app.name,
      is_affiliate: !!app.is_affiliate,
      partner: partner?.slug,
      partner_id: partner?.id,
    };

    externalLink(properties);
  };

  handleShareClick = (whichone, url) => {
    const {
      data: { app },
    } = this.props;

    analyticsShareClick({
      type: whichone,
      location: LOCATIONS_APP_PAGE,
      url,
      name: app.name,
      id_of_item: app.id,
    });
  };

  handleSubscribeOrManage = (isSubscribed, slug) => {
    const {
      router: { navigate },
      isIframe,
      params,
      partner,
    } = this.props;

    const partnerSettings = partner.settings || {};
    const isMybundleCheckout = partnerSettings.mybundle_checkout;
    const isUnsubscribedInIframe = isIframe && !isSubscribed;
    let url = getStringifiedUrl(
      getLink(isSubscribed ? ROUTE_PROFILE_SUBSCRIPTION_DETAIL : ROUTE_SUBSCRIPTION, {
        slug,
      }),
      {
        plan_id: params.get('plan_id') || '',
        addons: params.get('addons') || '',
      },
    );

    if (isMybundleCheckout) {
      url = siteUrl + url;
    }

    const routeEnhanced = routeEnhance(url, {
      state: { location: LOCATIONS_APP_PAGE },
      withoutIframe: isUnsubscribedInIframe,
    });

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    if (isUnsubscribedInIframe || isMybundleCheckout) {
      window.open(
        `${routeEnhanced.pathname}?${routeEnhanced.search}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      navigate(routeEnhanced);
    }
  };

  render() {
    const {
      t,
      router: {
        location: { state },
      },
      treatments,
      data,
      partner,
      loading,
      unique_id,
      segmentUserId,
      updateAppListStatus,
      statusesLoading,
      removeAppListStatus,
      isAuth,
      isSimpleView,
      user,
      virtualCard,
      hideExternal,
    } = this.props;
    const appData = data.app || {};
    const streamingRewardData = data.streaming_reward;
    const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || segmentUserId });
    const hasPartner = !!Object.keys(partner).length;
    const partnerSettings = partner?.settings || {};
    const isSubscribed = data.user_subscription !== null;
    const rewardData = user?.eligible_streaming_reward || {};
    const useVirtualCardForSubscription = Boolean(virtualCard?.allowed_for_subscriptions);

    const isFromDiscover =
      state &&
      (state.from === ROUTE_DISCOVER ||
        state.from === ROUTE_DISCOVER_SEARCH ||
        state.from === getLink(ROUTE_PARTNER_DISCOVER, { partner: partner.slug }));

    const slug = appData.unique_slug;

    let use_marketplace = true;
    let useAuthorizing = true;
    let useSubscription = true;
    let use_fmb = true;
    let fake = false;

    if (hasPartner) {
      use_marketplace = !!partnerSettings.use_marketplace;
      useAuthorizing = !!partnerSettings.use_authorizing;
      useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
      use_fmb = !!partnerSettings.use_fmb;
      fake = !!partnerSettings.is_fake;
    }

    const useSRCard =
      checkMultipleAppsRewardType(rewardData) &&
      ((hasPartner && partner.slug === rewardData.partner) ||
        (!hasPartner && rewardData.partner === null)) &&
      useVirtualCardForSubscription;
    const displayButtonsAppsList = useAuthorizing && !isSubscribed && !isSimpleView;
    const phone = appData.phone;
    const hasPhoneNumber = !!phone;
    const hasPhoneNumberStyles = hasPhoneNumber && (!hasPartner || displayButtonsAppsList);
    const showFMBBtn = !isFromDiscover && hasPartner && unique_id && use_fmb && !hideExternal;
    const appAdded = appData.list_status === appsListStatuses.have;
    const showBreadcrumbs = use_marketplace && !fake && !hideExternal;

    const subscriptionIsAvailable = useSubscription && appData.has_subscription && !isSimpleView;

    const shareLink = window.location.href;

    const facebookLink = `https://www.facebook.com/sharer/sharer.php?u=${shareLink}`;
    const twitterLink = `https://twitter.com/intent/tweet?text=${t(
      'appDetails.hero.share.twitter',
      {
        appName: appData.name,
      },
    )}&url=${shareLink}&related=mybundletv&hashtags=cutthecord,mybundletv`;
    const shareData = [
      {
        link: facebookLink,
        icon: 'facebook',
        title: 'Facebook',
        onClick: () => this.handleShareClick('facebook', facebookLink),
        id: 'fbShareWindow',
      },
      {
        link: twitterLink,
        icon: 'x',
        title: 'X',
        onClick: () => this.handleShareClick('twitter', twitterLink),
        id: 'twShareWindow',
      },
    ];

    const getVisitSiteBtn = (color = 'primary') => {
      return (
        <Button
          href={updateUrl(appData.url, externalUrlParams)}
          target="_blank"
          fullWidth
          color={color}
          onClick={this.handleVisitSite}
        >
          {appData.external_button_text ?? t('buttons.buyNow')}
        </Button>
      );
    };

    const getSubscribeBtn = () => {
      const variant = SubscriptionBtnNameVariant.getVariant(treatments);

      const startIcon =
        !isSubscribed && (useSRCard ? <CreditCardIcon /> : <i className="icon-mb-icon-2" />);
      const btnText = isSubscribed
        ? t('buttons.manage')
        : useSRCard
        ? t('streamingRewards.buyWithSRCard')
        : appData.external_button_text || t(`buttons.${variant}`);

      return (
        <Button
          fullWidth
          color="primary"
          variant="outlined"
          onClick={() => this.handleSubscribeOrManage(isSubscribed, slug)}
          startIcon={startIcon}
        >
          {btnText}
        </Button>
      );
    };

    return (
      <HeroContainer className="text-color--white hero-container" src={appData?.background}>
        <Darkness>
          <div className="page-content">
            {showBreadcrumbs && (
              <Hidden xsDown>
                <RouterBreadcrumbs
                  className="breadcrumbs breadcrumbs--light hero-container__breadcrumbs"
                  lastBreadcrumbText={appData.name}
                  exclude={['app']}
                />
              </Hidden>
            )}
            <Guard iframe={false} partner={{ hide_subtitle: false }}>
              <DisclaimerWrapper>
                <p className="disclamer-text">{t('appDetails.hero.disclaimer')}</p>
              </DisclaimerWrapper>
            </Guard>
            <Container>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={11} md={9} lg={8}>
                  <MainInfo>
                    {loading ? (
                      <>
                        <Skeleton className="skeleton skeleton__logo" />
                        <Skeleton className="skeleton skeleton__title" />
                        <div className="skeleton skeleton__desc">
                          <Skeleton />
                          <Skeleton />
                          <Skeleton />
                        </div>
                      </>
                    ) : (
                      <>
                        <AppLogo>
                          <img src={appData.thumbnail} alt={appData.meta_titles || appData.name} />
                        </AppLogo>
                        <Typography variant="h1">{appData.name}</Typography>
                        <AppDescription
                          text={appData.short_desc || ''}
                          applyLinkStyles
                          textStyles="p1"
                        />
                      </>
                    )}
                  </MainInfo>
                  <Grid item xs={12}>
                    <AnchorsContainer className="p1">
                      {loading ? (
                        <>
                          <Skeleton className="skeleton skeleton__anch" />
                          <i className="icon-vertical-slash" />
                          <Skeleton className="skeleton skeleton__anch" />
                          <i className="icon-vertical-slash" />
                          <Skeleton className="skeleton skeleton__anch" />
                        </>
                      ) : (
                        <>
                          {this.renderPrice()}
                          {this.renderAnchorLinks()}
                        </>
                      )}
                    </AnchorsContainer>
                  </Grid>

                  {!hideExternal && (
                    <Grid item xs={12}>
                      <ButtonsContainer
                        container
                        spacing={2}
                        className={clsx(
                          hasPhoneNumberStyles &&
                            !subscriptionIsAvailable &&
                            'buttons__phone-number',
                        )}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={hasPhoneNumberStyles && !subscriptionIsAvailable ? 12 : 'auto'}
                        >
                          {loading ? (
                            <Skeleton className="skeleton skeleton__btn" />
                          ) : (
                            <>
                              {subscriptionIsAvailable ? (
                                getSubscribeBtn()
                              ) : (
                                <>
                                  {hasPhoneNumber ? (
                                    <Button
                                      component="a"
                                      href={`tel:${phone}`}
                                      fullWidth
                                      color="primary"
                                      className="phone-number__button"
                                      startIcon={<CallIcon />}
                                    >
                                      {formatPhoneNumber(phone)}
                                    </Button>
                                  ) : (
                                    getVisitSiteBtn()
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Grid>

                        {hasPhoneNumber && !hasPhoneNumberStyles && !subscriptionIsAvailable && (
                          <Grid item xs={12} sm="auto">
                            {getVisitSiteBtn('secondary')}
                          </Grid>
                        )}

                        <Guard iframe={false}>
                          {displayButtonsAppsList && (
                            <Grid
                              item
                              xs={12}
                              sm={hasPhoneNumberStyles && !subscriptionIsAvailable ? 12 : 'auto'}
                            >
                              <div className="buttons-apps-list">
                                {loading ? (
                                  <Skeleton className="skeleton skeleton__btn skeleton__btn--have" />
                                ) : (
                                  <>
                                    <ButtonGroup color="primary" size="large" fullWidth>
                                      {hasPhoneNumberStyles &&
                                        !subscriptionIsAvailable &&
                                        getVisitSiteBtn()}

                                      {!partnerSettings.hide_have_button && (
                                        <AddRemoveButton
                                          onClick={() =>
                                            updateAppListStatus(appsListStatuses.have, isAuth)
                                          }
                                          handleDelete={removeAppListStatus}
                                          added={appAdded}
                                          minWidth={
                                            hasPhoneNumber && !subscriptionIsAvailable
                                              ? 'auto'
                                              : 195
                                          }
                                          checkAuth
                                          checkAuthProps={{
                                            authComponentProps: {
                                              hint: t('common.trackServices'),
                                              analyticsData: {
                                                triggered: triggered.app,
                                                action: actions.addToApps,
                                                app_id: appData.id,
                                                partner_id: partner?.id || 0,
                                                location: LOCATIONS_APP_PAGE,
                                              },
                                            },
                                          }}
                                        >
                                          {t('buttons.addToMyApps')}
                                        </AddRemoveButton>
                                      )}
                                    </ButtonGroup>

                                    {statusesLoading && <Loader active />}
                                  </>
                                )}
                              </div>
                            </Grid>
                          )}
                          {Boolean(streamingRewardData) && (
                            <Grid item xs={12} sm={hasPhoneNumberStyles ? 12 : 'auto'}>
                              <Button
                                className="reward__button"
                                component="a"
                                target="_blank"
                                href={streamingRewardData.url}
                                fullWidth
                                size="large"
                                color="primary"
                                variant="outlined"
                                location={LOCATIONS_APP_PAGE}
                                startIcon={<CreditCardIcon />}
                              >
                                {t('streamingRewards.buyWithSRCard')}
                              </Button>
                            </Grid>
                          )}
                        </Guard>
                        {showFMBBtn && (
                          <Grid item xs={12} sm="auto" className="grid-item__back-to-results">
                            <ButtonStyled
                              fullWidth
                              to={ROUTE_RESULTS}
                              component={Link}
                              location={LOCATIONS_APP_PAGE}
                            >
                              {t('buttons.liveTVResults')}
                            </ButtonStyled>
                          </Grid>
                        )}
                      </ButtonsContainer>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Container>
          </div>
        </Darkness>
        {!hasPartner && !loading && (
          <Hidden xsDown>
            <SocialWrapper className="social-wrapper">
              <SocialContainer>
                <Share
                  data={shareData}
                  copyLink={shareLink}
                  clickCopylink={() => this.handleShareClick('Copy Link', shareLink)}
                />
              </SocialContainer>
            </SocialWrapper>
          </Hidden>
        )}
      </HeroContainer>
    );
  }
}

Hero.defaultProps = {
  user: {},
  data: {},
  partner: {},
  statusesLoading: false,
  loading: false,
  hideExternal: false,
};

Hero.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  treatments: PropTypes.object.isRequired,
  user: PropTypes.object,
  isAuth: PropTypes.bool.isRequired,
  isSimpleView: PropTypes.bool.isRequired,
  isIframe: PropTypes.bool.isRequired,
  data: PropTypes.object,
  partner: PropTypes.object,
  unique_id: PropTypes.string.isRequired,
  segmentUserId: PropTypes.string.isRequired,
  updateAppListStatus: PropTypes.func.isRequired,
  removeAppListStatus: PropTypes.func.isRequired,
  statusesLoading: PropTypes.bool,
  params: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  virtualCard: PropTypes.object,
  hideExternal: PropTypes.bool,
};

export default compose(withRouter, withTranslation())(Hero);
